<template>
  <div class="bank-page">
    <div class="bank-shell">
      <header class="bank-head">
        <button type="button" class="head-btn" :aria-label="$t('auth.back')" @click="back">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M15 18l-6-6 6-6"></path>
          </svg>
        </button>

        <div class="head-copy">
          <h1>{{ copy.title }}</h1>
          <p>{{ copy.subtitle }}</p>
        </div>

        <button type="button" class="head-btn" :aria-label="$t('auth.home')" @click="goHome">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M3 11l9-8 9 8"></path>
            <path d="M5 10v10h14V10"></path>
          </svg>
        </button>
      </header>

      <main class="bank-content">
        <div v-if="hasBank" class="status-banner">
          {{ bankStatusNotice }}
        </div>

        <section v-if="hasBank" class="saved-card">
          <div class="saved-head">
            <span>{{ copy.savedTitle }}</span>
          </div>

          <div class="saved-body">
            <div class="saved-main">
              <h3>{{ bankName }}</h3>
              <p>{{ maskedBankNo }}</p>
              <small>{{ holderName }}</small>
            </div>

            <div class="saved-side">
              <em class="status-pill">{{ bankStatusText }}</em>
              <button type="button" class="delete-btn" @click="handleDeleteCard">
                {{ copy.deleteText }}
              </button>
            </div>
          </div>
        </section>

        <section class="form-card">
          <div class="form-field">
            <label>{{ copy.bankNameLabel }}</label>
            <button type="button" class="fake-input" @click="showBankPicker = true">
              <span :class="{ placeholder: !form.bankName }">
                {{ form.bankName || copy.bankNamePlaceholder || copy.bankPickerTitle }}
              </span>
            </button>
          </div>

          <div class="form-field">
            <label>{{ copy.cardNoLabel }}</label>
            <input
              v-model="form.cardNo"
              class="form-input"
              type="text"
              inputmode="numeric"
              autocomplete="off"
              :placeholder="copy.cardNoPlaceholder"
              @input="handleCardNoInput"
            />
          </div>

          <div class="form-field">
            <label>{{ copy.holderLabel }}</label>
            <input
              v-model="form.holderName"
              class="form-input"
              type="text"
              autocomplete="off"
              :placeholder="copy.holderPlaceholder"
            />
          </div>

          <button
            type="button"
            class="submit-btn"
            :disabled="submitting"
            @click="handlePrimarySubmit"
          >
            {{ submitting ? copy.submittingText : copy.submitText }}
          </button>
        </section>
      </main>
    </div>

    <van-popup v-model="showBankPicker" round position="bottom" :style="{ height: '40%' }">
      <van-picker
        show-toolbar
        :columns="bankColumns"
        :title="copy.bankPickerTitle"
        :confirm-button-text="copy.doneText || 'OK'"
        :cancel-button-text="copy.cancelText || 'Cancel'"
        @confirm="onBankConfirm"
        @cancel="showBankPicker = false"
      />
    </van-popup>

    <div v-if="showSecurityModal" class="modal-mask" @click.self="closeSecurityModal">
      <div class="modal-card">
        <h3>{{ copy.securityTitle }}</h3>
        <p>{{ copy.securityDesc }}</p>

        <input
          v-model.trim="security.mobile"
          class="modal-input"
          type="text"
          inputmode="numeric"
          autocomplete="off"
          :placeholder="copy.mobilePlaceholder"
        />
        <input
          v-model="security.payPassword"
          class="modal-input"
          type="password"
          autocomplete="off"
          :placeholder="copy.passwordPlaceholder"
        />
        <input
          v-model="security.confirmPassword"
          class="modal-input"
          type="password"
          autocomplete="off"
          :placeholder="copy.confirmPasswordPlaceholder"
        />

        <button type="button" class="modal-submit" :disabled="submitting" @click="submitBindCard">
          {{ submitting ? copy.submittingText : copy.confirmBindText }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { getPageCopy } from "@/common/pageLocale";

export default {
  data() {
    return {
      submitting: false,
      showSecurityModal: false,
      showBankPicker: false,
      userInfo: {},
      bankInfo: null,
      bankList: [],
      form: {
        bankName: "",
        cardNo: "",
        holderName: "",
        bankid: "",
      },
      editing: false,
      security: {
        mobile: "",
        payPassword: "",
        confirmPassword: "",
      },
    };
  },
  computed: {
    copy() {
      return getPageCopy(this, "setbank");
    },
    bankColumns() {
      return (this.bankList || []).map((item) => ({
        text: item.text || item.name || item.value || String(item.id),
        value: item.id != null ? item.id : item.value,
        raw: item,
      }));
    },
    hasBank() {
      return !!(this.bankInfo && (this.bankInfo.bank_code || this.bankInfo.bankid || this.bankInfo.bank_name || this.bankInfo.bankinfo));
    },
    bankName() {
      return (this.bankInfo && (this.bankInfo.bank_name || this.bankInfo.bankinfo)) || "--";
    },
    holderName() {
      return (this.bankInfo && this.bankInfo.username) || this.form.holderName || "--";
    },
    maskedBankNo() {
      const bankNo = (this.bankInfo && (this.bankInfo.bank_code || this.bankInfo.bankid)) || "";
      if (!bankNo) {
        return "****";
      }
      return `****${String(bankNo).slice(-4)}`;
    },
    bankStatusText() {
      const status = Number(this.bankInfo && this.bankInfo.status);
      if (status === 2) {
        return this.copy.statusApproved;
      }
      if (status === 1) {
        return this.copy.statusPending;
      }
      if (status === 3) {
        return this.copy.statusRejected;
      }
      return this.copy.statusApproved;
    },
    bankStatusNotice() {
      const status = Number(this.bankInfo && this.bankInfo.status);
      if (status === 2) {
        return `${this.copy.statusApproved}`;
      }
      if (status === 1) {
        return `${this.copy.statusPending}`;
      }
      if (status === 3) {
        return `${this.copy.statusRejected}`;
      }
      return this.copy.bankSubmitted;
    },
  },
  created() {
    if (!localStorage.getItem("token")) {
      this.$toast(this.copy.loginFirst);
      this.$router.replace("/Login");
      return;
    }
    this.bootstrap();
  },
  mounted() {
    document.title = `${this.copy.title} · OMN`;
  },
  methods: {
    async bootstrap() {
      await Promise.all([this.getUserInfo(), this.getBankList(), this.getUserBankInfo()]);
      if (this.userInfo && this.userInfo.name && !this.form.holderName) {
        this.form.holderName = this.userInfo.name;
      }
      if (this.userInfo && this.userInfo.mobile) {
        this.security.mobile = this.userInfo.mobile;
      }
    },
    async getUserInfo() {
      const res = await this.$http({
        method: "get",
        url: "user_info",
      });
      if (res && res.code === 200) {
        this.userInfo = res.data || {};
      }
    },
    async getBankList() {
      try {
        const res = await this.$http({
          method: "get",
          url: "sys_get_banks",
        });
        if (res && res.code === 200 && Array.isArray(res.data)) {
          this.bankList = res.data;
          return;
        }
        this.bankList = [];
      } catch (error) {
        this.bankList = [];
      }
    },
    async getUserBankInfo() {
      const res = await this.$http({
        method: "get",
        url: "user_get_bank",
      });
      if (res && res.code === 200 && res.data && res.data.is_bank) {
        this.bankInfo = res.data.info || null;
        if (this.bankInfo) {
          this.fillFormFromBank();
        }
      } else {
        this.bankInfo = null;
      }
    },
    fillFormFromBank() {
      if (!this.bankInfo) {
        return;
      }
      this.form.bankName = this.bankInfo.bank_name || this.bankInfo.bankinfo || this.form.bankName;
      this.form.cardNo = String(this.bankInfo.bank_code || "").replace(/\D/g, "");
      this.form.holderName = this.bankInfo.username || this.form.holderName;
      this.form.bankid = this.bankInfo.bankid || this.form.bankid;
      if (!this.form.bankName && this.form.bankid) {
        const matched = (this.bankList || []).find(
          (item) =>
            String(item.id) === String(this.form.bankid) ||
            String(item.value) === String(this.form.bankid)
        );
        if (matched) {
          this.form.bankName = matched.text || matched.name || this.form.bankName;
        }
      }
    },
    onBankConfirm(value) {
      const selected = value && value.raw ? value.raw : value;
      if (!selected) {
        this.showBankPicker = false;
        return;
      }
      this.form.bankName = selected.text || selected.name || "";
      this.form.bankid =
        selected.id != null
          ? selected.id
          : selected.value != null
            ? selected.value
            : value && value.value;
      this.showBankPicker = false;
    },
    back() {
      window.history.back();
    },
    goHome() {
      this.$router.push("/Home");
    },
    handleCardNoInput(event) {
      const raw = (event && event.target && event.target.value) || this.form.cardNo;
      this.form.cardNo = raw.replace(/\D/g, "");
    },
    handleDeleteCard() {
      this.editing = true;
      this.fillFormFromBank();
      if (this.copy.bankDeleteTip) {
        this.$toast(this.copy.bankDeleteTip);
      }
    },
    handlePrimarySubmit() {
      if (!this.form.bankid || !this.form.bankName) {
        this.$toast(this.copy.bankNameError);
        return;
      }
      if (!this.form.cardNo) {
        this.$toast(this.copy.cardNoError);
        return;
      }
      if (!this.form.holderName) {
        this.$toast(this.copy.holderError);
        return;
      }
      this.showSecurityModal = true;
    },
    closeSecurityModal() {
      if (this.submitting) {
        return;
      }
      this.showSecurityModal = false;
    },
    async submitBindCard() {
      if (!this.security.mobile) {
        this.$toast(this.copy.mobileError);
        return;
      }
      if (!this.security.payPassword || !this.security.confirmPassword) {
        this.$toast(this.copy.passwordError);
        return;
      }
      if (this.security.payPassword !== this.security.confirmPassword) {
        this.$toast(this.copy.passwordMismatch);
        return;
      }

      this.submitting = true;
      try {
        const res = await this.$http({
          method: "post",
          url: "user_set_bank",
          data: {
            bankid: this.form.bankid,
            bank: this.form.bankName,
            bank_code: this.form.cardNo,
            username: this.form.holderName,
            mobile: this.security.mobile,
            opw: this.security.payPassword,
            tpw: this.security.confirmPassword,
          },
        });
        if (res && res.code === 200) {
          this.$toast(res.msg || this.copy.bankSubmitted);
          this.showSecurityModal = false;
          this.editing = false;
          this.security.payPassword = "";
          this.security.confirmPassword = "";
          await this.getUserInfo();
          await this.getUserBankInfo();
          return;
        }
        this.$toast((res && res.msg) || this.copy.submitText);
      } catch (error) {
        this.$toast(this.copy.submitText);
      } finally {
        this.submitting = false;
      }
    },
  },
};
</script>

<style lang="less" scoped>
.bank-page {
  min-height: 100vh;
  min-height: 100dvh;
  background:
    radial-gradient(circle at 100% 0, rgba(255, 63, 120, 0.11), transparent 28%),
    radial-gradient(circle at 0 14%, rgba(165, 92, 255, 0.1), transparent 26%),
    #0a0710;
  color: #fff7fb;
}

.bank-shell {
  width: 100%;
  min-height: 100vh;
  min-height: 100dvh;
  max-width: none;
  margin: 0;
}

.bank-head {
  position: sticky;
  top: 0;
  z-index: 20;
  display: grid;
  grid-template-columns: 2.625rem 1fr 2.625rem;
  align-items: center;
  min-height: 4.1875rem;
  padding: max(0.5rem, env(safe-area-inset-top)) 0.8125rem 0.5rem;
  background: rgba(10, 7, 16, 0.92);
  border-bottom: 1px solid rgba(255, 255, 255, 0.085);
  backdrop-filter: blur(1.125rem);
  -webkit-backdrop-filter: blur(1.125rem);
}

.head-btn {
  width: 2.375rem;
  height: 2.375rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(255, 255, 255, 0.085);
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.045);
  color: #fff;
}

.head-btn svg {
  width: 1.1875rem;
  height: 1.1875rem;
}

.head-copy h1 {
  margin: 0;
  text-align: center;
  font-size: 1.125rem;
  font-weight: 900;
  letter-spacing: -0.0125rem;
}

.head-copy p {
  margin: 0.1875rem 0 0;
  text-align: center;
  color: #a89baa;
  font-size: 0.5625rem;
  line-height: 1.4;
}

.bank-content {
  padding: 1rem;
}

.status-banner {
  display: flex;
  align-items: center;
  min-height: 2.5rem;
  padding: 0 0.9375rem;
  margin-bottom: 0.875rem;
  border: 1px solid rgba(77, 227, 165, 0.32);
  border-radius: 0.9375rem;
  background: linear-gradient(135deg, rgba(77, 227, 165, 0.08), rgba(70, 180, 255, 0.05));
  color: #67e7b3;
  font-size: 0.75rem;
  font-weight: 800;
}

.saved-card,
.form-card {
  border-radius: 1.375rem;
  background: rgba(255, 255, 255, 0.045);
  border: 1px solid rgba(255, 255, 255, 0.085);
  box-shadow: 0 0.8125rem 2.125rem rgba(0, 0, 0, 0.15);
}

.saved-card {
  padding: 1rem;
  margin-bottom: 0.875rem;
}

.saved-head {
  color: #fff;
  font-size: 0.75rem;
  font-weight: 800;
}

.saved-body {
  display: flex;
  justify-content: space-between;
  gap: 0.875rem;
  margin-top: 0.875rem;
}

.saved-main {
  min-width: 0;
  flex: 1;
}

.saved-main h3 {
  margin: 0;
  font-size: 1.125rem;
  font-weight: 900;
}

.saved-main p {
  margin: 0.5rem 0 0;
  color: #ffd9ee;
  font-size: 0.8125rem;
  letter-spacing: 0.04em;
}

.saved-main small {
  display: block;
  margin-top: 0.375rem;
  color: #b1a4b2;
  font-size: 0.75rem;
}

.saved-side {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.625rem;
}

.status-pill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 3.375rem;
  height: 1.5rem;
  padding: 0 0.625rem;
  border-radius: 999px;
  background: rgba(255, 198, 92, 0.14);
  color: #ffc65c;
  font-size: 0.625rem;
  font-style: normal;
  font-weight: 900;
}

.delete-btn {
  border: 0;
  padding: 0;
  background: transparent;
  color: #ff6d9c;
  font-size: 0.75rem;
  font-weight: 800;
}

.form-card {
  padding: 1rem;
}

.form-field + .form-field {
  margin-top: 0.875rem;
}

.form-field label {
  display: block;
  margin-bottom: 0.4375rem;
  color: #fff;
  font-size: 0.75rem;
  font-weight: 800;
}

.form-input,
.fake-input {
  width: 100%;
  height: 3.125rem;
  border-radius: 1rem;
  border: 1px solid rgba(255, 255, 255, 0.085);
  background: rgba(255, 255, 255, 0.05);
  color: #fff;
  padding: 0 0.875rem;
  outline: none;
}

.form-input::placeholder {
  color: #776b76;
}

.fake-input {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  text-align: left;
}

.fake-input span.placeholder {
  color: #776b76;
}

.submit-btn {
  width: 100%;
  height: 3.125rem;
  margin-top: 1.5rem;
  border: 0;
  border-radius: 1.5625rem;
  background: linear-gradient(135deg, #ff6d9c, #ff3f78);
  box-shadow: 0 0.6875rem 1.625rem rgba(255, 63, 120, 0.24);
  color: #fff;
  font-size: 1.0625rem;
  font-weight: 900;
}

.submit-btn[disabled] {
  opacity: 0.6;
}

.modal-mask {
  position: fixed;
  inset: 0;
  z-index: 80;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  background: rgba(3, 2, 5, 0.76);
  backdrop-filter: blur(0.5625rem);
}

.modal-submit,
.head-btn,
.delete-btn,
.submit-btn {
  cursor: pointer;
}

.modal-mask {
  align-items: center;
  padding: 1.25rem;
}

.modal-card {
  width: 100%;
  max-width: 24.375rem;
  border-radius: 1.5rem;
  padding: 1.25rem;
  background: #1a1221;
  border: 1px solid rgba(255, 255, 255, 0.12);
  box-shadow: 0 1.375rem 4.375rem rgba(0, 0, 0, 0.5);
}

.modal-card h3 {
  margin: 0;
  font-size: 1.125rem;
  font-weight: 900;
}

.modal-card p {
  margin: 0.625rem 0 0;
  color: #c9bbc6;
  font-size: 0.75rem;
  line-height: 1.7;
}

.modal-input {
  width: 100%;
  height: 2.875rem;
  margin-top: 0.875rem;
  border: 1px solid rgba(255, 255, 255, 0.085);
  border-radius: 0.9375rem;
  background: rgba(255, 255, 255, 0.05);
  color: #fff;
  padding: 0 0.875rem;
  outline: none;
}

.modal-input::placeholder {
  color: #776b76;
}

.modal-submit {
  width: 100%;
  height: 2.875rem;
  margin-top: 1rem;
  border: 0;
  border-radius: 0.9375rem;
  background: linear-gradient(135deg, #ff6d9c, #ff3f78);
  color: #fff;
  font-size: 0.9375rem;
  font-weight: 900;
}

.modal-submit[disabled] {
  opacity: 0.6;
}

@media (min-width: 48rem) {
  .bank-shell {
    max-width: 25rem;
    margin: 0 auto;
  }
}
</style>
